import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, BarChart, Bar } from 'recharts';
import { Trophy, TrendingUp, PieChart as PieIcon, AlertTriangle, UserX, Heart, Clock } from 'lucide-react';

const DONUT_COLORS = ['#2A7B88', '#3B5BA9', '#2D8B5E', '#BA7517', '#D4726A', '#7C3AED', '#D97706', '#0891B2', '#059669', '#6366F1'];
const fmtCurrency = (v) => {
  if (!v) return '0';
  if (v >= 10000000) return `${(v / 10000000).toFixed(2).replace(/0+$/, '').replace(/\.$/, '')}Cr`;
  if (v >= 100000) return `${(v / 100000).toFixed(1)}L`;
  return Number(v).toLocaleString('en-IN');
};

const PERIOD_OPTIONS = [
  { value: 'daily', label: 'Today' },
  { value: 'weekly', label: 'This Week' },
  { value: 'monthly', label: 'This Month' },
  { value: 'quarterly', label: 'This Quarter' },
  { value: 'ytd', label: 'FY YTD' },
];

const TREND_PERIOD_OPTIONS = [
  { value: 'last_5d', label: '5 Days' },
  { value: 'last_7d', label: '7 Days' },
  { value: 'last_30d', label: '30 Days' },
  { value: 'last_90d', label: '90 Days' },
  { value: 'last_6m', label: '6 Months' },
  { value: 'last_1y', label: '1 Year' },
  { value: 'last_3y', label: '3 Years' },
  { value: 'last_5y', label: '5 Years' },
  { value: 'ytd', label: 'FY YTD' },
];

// Rich tooltip showing per-outlet breakdown (candlestick style)
const RichTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const d = payload[0]?.payload;
  const outlets = d?.by_outlet || {};
  const sorted = Object.entries(outlets).sort((a, b) => b[1] - a[1]);
  return (
    <div className="bg-white border rounded-lg shadow-lg text-xs min-w-[180px]" style={{ borderColor: '#E5E1DC' }}>
      <div className="px-3 py-2 border-b" style={{ borderColor: '#F3F4F6' }}>
        <p className="font-medium" style={{ color: '#1B2A4A' }}>{d?.fullLabel}</p>
        <p className="font-bold text-sm" style={{ color: '#2A7B88' }}>{fmtCurrency(d?.value)}</p>
      </div>
      {sorted.length > 0 && (
        <div className="px-3 py-2 space-y-1">
          {sorted.slice(0, 8).map(([name, val], i) => (
            <div key={name} className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-1.5 min-w-0">
                <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ backgroundColor: DONUT_COLORS[i % DONUT_COLORS.length] }} />
                <span className="truncate" style={{ color: '#6B7280' }}>{name}</span>
              </div>
              <span className="font-medium shrink-0" style={{ color: '#1B2A4A' }}>{fmtCurrency(val)}</span>
            </div>
          ))}
          {sorted.length > 8 && <p className="text-[9px]" style={{ color: '#9CA3AF' }}>+{sorted.length - 8} more</p>}
        </div>
      )}
    </div>
  );
};


// --- Mini Leaderboard (for summary on main pages) ---
export function MiniLeaderboard({ divisionId, limit = 5 }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [period, setPeriod] = useState('monthly');

  useEffect(() => {
    const params = divisionId ? `division_id=${divisionId}&period=${period}` : `period=${period}`;
    api.get(`/daily-sales/leaderboard?${params}`).then(r => setData(r.data)).catch(() => {});
  }, [divisionId, period]);

  if (!data || !data.outlets?.length) return null;
  const items = data.outlets.slice(0, limit);

  return (
    <div className="bg-white rounded-[10px] border p-4" style={{ borderColor: '#E5E1DC' }} data-testid="mini-leaderboard">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Trophy size={14} style={{ color: '#BA7517' }} />
          <h4 className="text-sm font-semibold" style={{ color: '#1B2A4A' }}>Outlet Leaderboard</h4>
        </div>
        <select value={period} onChange={e => setPeriod(e.target.value)}
          className="text-[11px] border rounded-md px-2 py-1" style={{ borderColor: '#E5E1DC', color: '#6B7280' }}
          data-testid="leaderboard-period">
          {PERIOD_OPTIONS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
        </select>
      </div>
      <div className="space-y-1.5">
        {items.map((o, i) => {
          const barColor = o.pct >= 30 ? '#2D8B5E' : o.pct >= 15 ? '#BA7517' : '#D4726A';
          return (
            <div key={o.outlet} className="relative" data-testid={`lb-row-${i}`}>
              <div className="flex items-center justify-between text-xs py-1.5 px-2 rounded-md relative z-10"
                style={{ backgroundColor: i === 0 ? '#FEF3C7' : i < 3 ? '#F7F5F2' : 'transparent' }}>
                <div className="flex items-center gap-2 min-w-0 flex-1">
                  <span className="text-[10px] font-bold w-4 text-center" style={{ color: i < 3 ? '#BA7517' : '#9CA3AF' }}>
                    {i + 1}
                  </span>
                  <span className="font-medium truncate cursor-pointer hover:text-[#2A7B88] hover:underline" style={{ color: '#1B2A4A' }}
                    onClick={() => divisionId && navigate(`/division/${divisionId}/outlet/${encodeURIComponent(o.outlet)}`)}>{o.outlet}</span>
                  {!divisionId && <span className="text-[9px] px-1 py-0.5 rounded" style={{ backgroundColor: '#E6F1FB', color: '#3B5BA9' }}>{o.division_name}</span>}
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <span className="font-medium" style={{ color: '#1B2A4A' }}>{fmtCurrency(o.actual)}</span>
                  <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full"
                    style={{ backgroundColor: barColor + '15', color: barColor }}>
                    {o.pct}%
                  </span>
                </div>
              </div>
              <div className="absolute bottom-0 left-0 h-[2px] rounded-full" style={{ width: `${Math.min(o.pct, 100)}%`, backgroundColor: barColor, opacity: 0.4 }} />
            </div>
          );
        })}
      </div>
      <p className="text-[9px] mt-2 text-right" style={{ color: '#9CA3AF' }}>
        {data.date_from} to {data.date_to} · % of target
      </p>
    </div>
  );
}


// --- Mini Revenue Trend (for summary on main pages) ---
export function MiniRevenueTrend({ divisionId }) {
  const [data, setData] = useState(null);
  const [period, setPeriod] = useState('last_6m');
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams({ period });
    if (divisionId) params.set('division_id', divisionId);
    api.get(`/daily-sales/revenue-trend?${params}`).then(r => { setData(r.data); setLoaded(true); }).catch(() => setLoaded(true));
  }, [divisionId, period]);

  if (!loaded) return null;

  const chartData = (data?.data || []).map(d => ({
    name: data?.granularity === 'monthly' ? d.label.split('-')[1] + '/' + d.label.split('-')[0].slice(2)
      : data?.granularity === 'weekly' ? d.label.slice(5)
      : d.label.slice(5),
    value: d.total,
    fullLabel: d.label,
    by_outlet: d.by_outlet || {},
  }));

  return (
    <div className="bg-white rounded-[10px] border p-4" style={{ borderColor: '#E5E1DC' }} data-testid="mini-revenue-trend">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <TrendingUp size={14} style={{ color: '#2A7B88' }} />
          <h4 className="text-sm font-semibold" style={{ color: '#1B2A4A' }}>Revenue Trend</h4>
        </div>
        <select value={period} onChange={e => setPeriod(e.target.value)}
          className="text-[11px] border rounded-md px-2 py-1" style={{ borderColor: '#E5E1DC', color: '#6B7280' }}
          data-testid="trend-period">
          {TREND_PERIOD_OPTIONS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
        </select>
      </div>
      {chartData.length > 0 ? (
        <div style={{ height: 180 }}>
          <ResponsiveContainer width="100%" height="100%" initialDimension={{ width: 1, height: 1 }}>
            <AreaChart data={chartData} margin={{ top: 5, right: 5, bottom: 0, left: 5 }}>
              <defs>
                <linearGradient id={`trendGrad-${divisionId || 'ceo'}`} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#2A7B88" stopOpacity={0.2} />
                  <stop offset="95%" stopColor="#2A7B88" stopOpacity={0} />
                </linearGradient>
              </defs>
              <XAxis dataKey="name" tick={{ fontSize: 10, fill: '#9CA3AF' }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 10, fill: '#9CA3AF' }} axisLine={false} tickLine={false}
                tickFormatter={v => fmtCurrency(v)} width={50} />
              <Tooltip content={<RichTooltip />} />
              <Area type="monotone" dataKey="value" stroke="#2A7B88" strokeWidth={2}
                fill={`url(#trendGrad-${divisionId || 'ceo'})`} dot={{ r: 3, fill: '#2A7B88', stroke: '#fff', strokeWidth: 2 }} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      ) : (
        <div className="flex items-center justify-center" style={{ height: 180 }}>
          <p className="text-xs" style={{ color: '#9CA3AF' }}>No daily sales data yet. Import CSV or enter sales to see trends.</p>
        </div>
      )}
      <p className="text-[9px] mt-1 text-right" style={{ color: '#9CA3AF' }}>
        {data?.granularity || ''} · {data?.date_from || ''} to {data?.date_to || ''}
      </p>
    </div>
  );
}


// --- Mini Contribution Donut (for summary on main pages) ---
export function MiniContribution({ divisionId }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [period, setPeriod] = useState('monthly');
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams({ period });
    if (divisionId) params.set('division_id', divisionId);
    api.get(`/daily-sales/contribution?${params}`).then(r => { setData(r.data); setLoaded(true); }).catch(() => setLoaded(true));
  }, [divisionId, period]);

  if (!loaded) return null;

  return (
    <div className="bg-white rounded-[10px] border p-4" style={{ borderColor: '#E5E1DC' }} data-testid="mini-contribution">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <PieIcon size={14} style={{ color: '#3B5BA9' }} />
          <h4 className="text-sm font-semibold" style={{ color: '#1B2A4A' }}>
            {divisionId ? 'Outlet' : 'Division'} Contribution
          </h4>
        </div>
        <select value={period} onChange={e => setPeriod(e.target.value)}
          className="text-[11px] border rounded-md px-2 py-1" style={{ borderColor: '#E5E1DC', color: '#6B7280' }}
          data-testid="contribution-period">
          {PERIOD_OPTIONS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
        </select>
      </div>
      {data?.segments?.length > 0 ? (
        <div className="flex items-center gap-4">
          <div style={{ width: 160, height: 160 }}>
            <ResponsiveContainer width="100%" height="100%" initialDimension={{ width: 1, height: 1 }}>
              <PieChart>
                <Pie data={data.segments} cx="50%" cy="50%" innerRadius={40} outerRadius={70}
                  dataKey="value" labelLine={false}>
                  {data.segments.map((s, i) => (
                    <Cell key={s.name} fill={DONUT_COLORS[i % DONUT_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip formatter={v => fmtCurrency(v)} />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="flex-1 space-y-1 min-w-0">
            {data.segments.slice(0, 6).map((s, i) => (
              <div key={s.name} className="flex items-center gap-1.5 text-[10px]">
                <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: DONUT_COLORS[i % DONUT_COLORS.length] }} />
                <span className="truncate cursor-pointer hover:text-[#2A7B88] hover:underline" style={{ color: '#6B7280' }}
                  onClick={() => divisionId && navigate(`/division/${divisionId}/outlet/${encodeURIComponent(s.name)}`)}>{s.name}</span>
                <span className="ml-auto font-medium shrink-0" style={{ color: '#1B2A4A' }}>{fmtCurrency(s.value)}</span>
              </div>
            ))}
            {data.segments.length > 6 && <p className="text-[9px]" style={{ color: '#9CA3AF' }}>+{data.segments.length - 6} more</p>}
            <div className="pt-1 border-t" style={{ borderColor: '#F3F4F6' }}>
              <p className="text-[10px] font-semibold" style={{ color: '#1B2A4A' }}>Total: {fmtCurrency(data.total)}</p>
            </div>
          </div>
        </div>
      ) : (
        <div className="flex items-center justify-center" style={{ height: 160 }}>
          <p className="text-xs text-center" style={{ color: '#9CA3AF' }}>No data yet for this period.<br />Enter daily sales or import CSV.</p>
        </div>
      )}
    </div>
  );
}


// Custom Candlestick Bar Shape
const CandlestickShape = (props) => {
  const { x, y, width, height, payload } = props;
  if (!payload) return null;
  const isGreen = payload.close >= payload.open;
  const color = isGreen ? '#2D8B5E' : '#D4726A';
  const bodyTop = Math.min(payload.open, payload.close);
  const bodyBottom = Math.max(payload.open, payload.close);
  const yScale = height > 0 ? (props.background?.height || 250) / (props.background?.y || 1) : 1;

  return (
    <g>
      {/* Body */}
      <rect x={x + width * 0.15} y={y} width={width * 0.7} height={Math.max(height, 3)}
        fill={color} rx={1} opacity={0.85} />
      {/* Wick top */}
      <line x1={x + width / 2} y1={y - 4} x2={x + width / 2} y2={y}
        stroke={color} strokeWidth={1.5} />
      {/* Wick bottom */}
      <line x1={x + width / 2} y1={y + Math.max(height, 3)} x2={x + width / 2} y2={y + Math.max(height, 3) + 4}
        stroke={color} strokeWidth={1.5} />
    </g>
  );
};


// --- Full Analytics Tab ---
export default function AnalyticsTab({ divisionId, divisionName }) {
  const [leaderboard, setLeaderboard] = useState(null);
  const [trend, setTrend] = useState(null);
  const [contribution, setContribution] = useState(null);
  const [lbPeriod, setLbPeriod] = useState('monthly');
  const [trendPeriod, setTrendPeriod] = useState('last_6m');
  const [contribPeriod, setContribPeriod] = useState('monthly');
  const [chartMode, setChartMode] = useState('area'); // 'area' or 'candlestick'
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [showCustomDates, setShowCustomDates] = useState(false);

  useEffect(() => {
    const params = divisionId ? `division_id=${divisionId}&` : '';
    api.get(`/daily-sales/leaderboard?${params}period=${lbPeriod}`).then(r => setLeaderboard(r.data)).catch(() => {});
  }, [divisionId, lbPeriod]);

  const fetchTrend = (p, from, to) => {
    const params = new URLSearchParams();
    if (divisionId) params.set('division_id', divisionId);
    if (from && to) {
      params.set('date_from', from);
      params.set('date_to', to);
    } else {
      params.set('period', p);
    }
    api.get(`/daily-sales/revenue-trend?${params}`).then(r => setTrend(r.data)).catch(() => {});
  };

  useEffect(() => {
    if (!showCustomDates) fetchTrend(trendPeriod);
  }, [divisionId, trendPeriod]);

  const applyCustomDates = () => {
    if (customFrom && customTo) fetchTrend('custom', customFrom, customTo);
  };

  useEffect(() => {
    const params = new URLSearchParams({ period: contribPeriod });
    if (divisionId) params.set('division_id', divisionId);
    api.get(`/daily-sales/contribution?${params}`).then(r => setContribution(r.data)).catch(() => {});
  }, [divisionId, contribPeriod]);

  const trendChartData = (trend?.data || []).map(d => ({
    name: trend?.granularity === 'monthly' ? d.label
      : trend?.granularity === 'weekly' ? d.label.slice(5)
      : d.label.slice(5),
    value: d.total,
    fullLabel: d.label,
    by_outlet: d.by_outlet || {},
    open: d.open || 0,
    close: d.close || d.total || 0,
    high: d.high || 0,
    low: d.low || 0,
    change: d.change || 0,
    change_pct: d.change_pct || 0,
    // For candlestick bar sizing
    barBottom: Math.min(d.open || 0, d.close || d.total || 0),
    barHeight: Math.abs((d.close || d.total || 0) - (d.open || 0)) || 1,
  }));

  // Candlestick tooltip
  const CandlestickTooltip = ({ active, payload }) => {
    if (!active || !payload?.length) return null;
    const d = payload[0]?.payload;
    const isUp = d?.close >= d?.open;
    return (
      <div className="bg-white border rounded-lg shadow-lg text-xs min-w-[200px]" style={{ borderColor: '#E5E1DC' }}>
        <div className="px-3 py-2 border-b" style={{ borderColor: '#F3F4F6' }}>
          <p className="font-medium" style={{ color: '#1B2A4A' }}>{d?.fullLabel}</p>
          <div className="flex items-center gap-2 mt-1">
            <span className="font-bold text-sm" style={{ color: isUp ? '#2D8B5E' : '#D4726A' }}>{fmtCurrency(d?.close)}</span>
            <span className="text-[10px] px-1.5 py-0.5 rounded-full font-semibold"
              style={{ backgroundColor: isUp ? '#ECFDF5' : '#FEF2F2', color: isUp ? '#2D8B5E' : '#D4726A' }}>
              {isUp ? '+' : ''}{d?.change_pct}%
            </span>
          </div>
        </div>
        <div className="px-3 py-2 grid grid-cols-2 gap-x-4 gap-y-1">
          <div><span style={{ color: '#9CA3AF' }}>Prev:</span> <span className="font-medium">{fmtCurrency(d?.open)}</span></div>
          <div><span style={{ color: '#9CA3AF' }}>Current:</span> <span className="font-medium">{fmtCurrency(d?.close)}</span></div>
          <div><span style={{ color: '#9CA3AF' }}>Best outlet:</span> <span className="font-medium">{fmtCurrency(d?.high)}</span></div>
          <div><span style={{ color: '#9CA3AF' }}>Lowest outlet:</span> <span className="font-medium">{fmtCurrency(d?.low)}</span></div>
        </div>
        {d?.by_outlet && Object.keys(d.by_outlet).length > 0 && (
          <div className="px-3 py-2 border-t space-y-0.5" style={{ borderColor: '#F3F4F6' }}>
            {Object.entries(d.by_outlet).sort((a, b) => b[1] - a[1]).slice(0, 6).map(([name, val], i) => (
              <div key={name} className="flex items-center justify-between">
                <span className="flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: DONUT_COLORS[i % DONUT_COLORS.length] }} />{name}</span>
                <span className="font-medium">{fmtCurrency(val)}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-4" data-testid="analytics-tab">
      {/* Revenue Trend - Full */}
      <div className="bg-white rounded-[10px] border p-5" style={{ borderColor: '#E5E1DC' }}>
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="flex items-center gap-3">
              <h4 className="text-sm font-semibold" style={{ color: '#1B2A4A' }}>Revenue Trend</h4>
              {/* Area / Candlestick toggle */}
              <div className="flex rounded-md overflow-hidden border" style={{ borderColor: '#E5E1DC' }}>
                <button onClick={() => setChartMode('area')}
                  className="px-2 py-0.5 text-[10px] font-medium transition-all"
                  style={{ backgroundColor: chartMode === 'area' ? '#1B2A4A' : '#fff', color: chartMode === 'area' ? '#fff' : '#6B7280' }}
                  data-testid="chart-mode-area">Area</button>
                <button onClick={() => setChartMode('candlestick')}
                  className="px-2 py-0.5 text-[10px] font-medium transition-all"
                  style={{ backgroundColor: chartMode === 'candlestick' ? '#1B2A4A' : '#fff', color: chartMode === 'candlestick' ? '#fff' : '#6B7280' }}
                  data-testid="chart-mode-candlestick">Candlestick</button>
              </div>
            </div>
            <p className="text-[10px]" style={{ color: '#9CA3AF' }}>
              {trend?.granularity || 'monthly'} view · {trend?.date_from} to {trend?.date_to}
              {chartMode === 'area' ? ' · Hover for per-outlet breakdown' : ' · Green=up Red=down vs previous'}
            </p>
          </div>
          <div className="flex flex-col items-end gap-1">
            <div className="flex flex-wrap gap-1">
              {TREND_PERIOD_OPTIONS.map(p => (
                <button key={p.value} onClick={() => { setTrendPeriod(p.value); setShowCustomDates(false); }}
                  className="px-2.5 py-1 rounded-full text-[10px] transition-all"
                  style={{
                    background: !showCustomDates && trendPeriod === p.value ? '#1B2A4A' : 'transparent',
                    color: !showCustomDates && trendPeriod === p.value ? '#fff' : '#6B7280',
                    border: `0.5px solid ${!showCustomDates && trendPeriod === p.value ? '#1B2A4A' : '#E5E1DC'}`,
                  }} data-testid={`trend-pill-${p.value}`}>
                  {p.label}
                </button>
              ))}
              <button onClick={() => setShowCustomDates(!showCustomDates)}
                className="px-2.5 py-1 rounded-full text-[10px] transition-all"
                style={{
                  background: showCustomDates ? '#2A7B88' : 'transparent',
                  color: showCustomDates ? '#fff' : '#6B7280',
                  border: `0.5px solid ${showCustomDates ? '#2A7B88' : '#E5E1DC'}`,
                }} data-testid="trend-custom-dates">
                Custom
              </button>
            </div>
            {showCustomDates && (
              <div className="flex items-center gap-2 mt-1">
                <input type="date" value={customFrom} onChange={e => setCustomFrom(e.target.value)}
                  className="text-[11px] border rounded px-2 py-1" style={{ borderColor: '#E5E1DC' }} data-testid="trend-date-from" />
                <span className="text-[10px]" style={{ color: '#9CA3AF' }}>to</span>
                <input type="date" value={customTo} onChange={e => setCustomTo(e.target.value)}
                  className="text-[11px] border rounded px-2 py-1" style={{ borderColor: '#E5E1DC' }} data-testid="trend-date-to" />
                <button onClick={applyCustomDates} disabled={!customFrom || !customTo}
                  className="px-2.5 py-1 rounded text-[10px] font-medium text-white disabled:opacity-40"
                  style={{ backgroundColor: '#2A7B88' }} data-testid="trend-apply-dates">
                  Apply
                </button>
              </div>
            )}
          </div>
        </div>
        {trendChartData.length > 0 ? (
          <div style={{ height: 280 }}>
            <ResponsiveContainer width="100%" height="100%" initialDimension={{ width: 1, height: 1 }}>
              {chartMode === 'area' ? (
                <AreaChart data={trendChartData} margin={{ top: 10, right: 10, bottom: 0, left: 10 }}>
                  <defs>
                    <linearGradient id="fullTrendGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#2A7B88" stopOpacity={0.15} />
                      <stop offset="95%" stopColor="#2A7B88" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <XAxis dataKey="name" tick={{ fontSize: 11, fill: '#9CA3AF' }} axisLine={{ stroke: '#E5E1DC' }} tickLine={false} />
                  <YAxis tick={{ fontSize: 11, fill: '#9CA3AF' }} axisLine={false} tickLine={false}
                    tickFormatter={v => fmtCurrency(v)} width={55} />
                  <Tooltip content={<RichTooltip />} />
                  <Area type="monotone" dataKey="value" stroke="#2A7B88" strokeWidth={2.5}
                    fill="url(#fullTrendGrad)" dot={{ r: 4, fill: '#2A7B88', stroke: '#fff', strokeWidth: 2 }}
                    activeDot={{ r: 6, fill: '#2A7B88', stroke: '#fff', strokeWidth: 2 }} />
                </AreaChart>
              ) : (
                <BarChart data={trendChartData} margin={{ top: 10, right: 10, bottom: 0, left: 10 }}>
                  <XAxis dataKey="name" tick={{ fontSize: 11, fill: '#9CA3AF' }} axisLine={{ stroke: '#E5E1DC' }} tickLine={false} />
                  <YAxis tick={{ fontSize: 11, fill: '#9CA3AF' }} axisLine={false} tickLine={false}
                    tickFormatter={v => fmtCurrency(v)} width={55} />
                  <Tooltip content={<CandlestickTooltip />} />
                  <Bar dataKey="value" shape={(props) => {
                    const { x, y, width, height, payload } = props;
                    if (!payload) return null;
                    const isGreen = (payload.change || 0) >= 0;
                    const color = isGreen ? '#2D8B5E' : '#D4726A';
                    return (
                      <g>
                        <rect x={x + width * 0.2} y={y} width={width * 0.6} height={Math.max(height, 4)}
                          fill={color} rx={2} opacity={0.85} />
                        <line x1={x + width / 2} y1={y - 6} x2={x + width / 2} y2={y}
                          stroke={color} strokeWidth={1.5} />
                        <line x1={x + width / 2} y1={y + Math.max(height, 4)} x2={x + width / 2} y2={y + Math.max(height, 4) + 6}
                          stroke={color} strokeWidth={1.5} />
                      </g>
                    );
                  }} />
                </BarChart>
              )}
            </ResponsiveContainer>
          </div>
        ) : (
          <div className="flex items-center justify-center" style={{ height: 280 }}>
            <div className="text-center">
              <p className="text-sm" style={{ color: '#9CA3AF' }}>No revenue data for this period</p>
              <p className="text-xs mt-1" style={{ color: '#B0B0B0' }}>Import historical CSV or enter daily sales to see trends</p>
            </div>
          </div>
        )}
      </div>

      {/* Contribution + Leaderboard side by side */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Contribution Donut - Full */}
        <div className="bg-white rounded-[10px] border p-5" style={{ borderColor: '#E5E1DC' }}>
          <div className="flex items-center justify-between mb-4">
            <h4 className="text-sm font-semibold" style={{ color: '#1B2A4A' }}>
              {divisionId ? 'Outlet' : 'Division'} Contribution
            </h4>
            <select value={contribPeriod} onChange={e => setContribPeriod(e.target.value)}
              className="text-[11px] border rounded-md px-2 py-1" style={{ borderColor: '#E5E1DC', color: '#6B7280' }}>
              {PERIOD_OPTIONS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
            </select>
          </div>
          {contribution?.segments?.length > 0 ? (
            <>
              <div style={{ height: 220 }}>
                <ResponsiveContainer width="100%" height="100%" initialDimension={{ width: 1, height: 1 }}>
                  <PieChart>
                    <Pie data={contribution.segments} cx="50%" cy="50%" innerRadius={55} outerRadius={95}
                      dataKey="value" labelLine={false}>
                      {contribution.segments.map((s, i) => (
                        <Cell key={s.name} fill={DONUT_COLORS[i % DONUT_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={v => fmtCurrency(v)} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="grid grid-cols-2 gap-x-4 gap-y-1 mt-2">
                {contribution.segments.map((s, i) => (
                  <div key={s.name} className="flex items-center gap-1.5 text-[11px]">
                    <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: DONUT_COLORS[i % DONUT_COLORS.length] }} />
                    <span className="truncate" style={{ color: '#6B7280' }}>{s.name}</span>
                    <span className="ml-auto font-medium shrink-0" style={{ color: '#1B2A4A' }}>{s.pct}%</span>
                  </div>
                ))}
              </div>
              <div className="mt-3 pt-2 border-t text-center" style={{ borderColor: '#F3F4F6' }}>
                <p className="text-xs font-bold" style={{ color: '#1B2A4A' }}>Total: {fmtCurrency(contribution.total)}</p>
              </div>
            </>
          ) : (
            <div className="flex items-center justify-center" style={{ height: 220 }}>
              <p className="text-xs text-center" style={{ color: '#9CA3AF' }}>No data for this period.<br />Import CSV or enter daily sales.</p>
            </div>
          )}
        </div>

        {/* Full Leaderboard */}
        <div className="bg-white rounded-[10px] border p-5" style={{ borderColor: '#E5E1DC' }}>
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Trophy size={14} style={{ color: '#BA7517' }} />
              <h4 className="text-sm font-semibold" style={{ color: '#1B2A4A' }}>Full Leaderboard</h4>
            </div>
            <select value={lbPeriod} onChange={e => setLbPeriod(e.target.value)}
              className="text-[11px] border rounded-md px-2 py-1" style={{ borderColor: '#E5E1DC', color: '#6B7280' }}>
              {PERIOD_OPTIONS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
            </select>
          </div>
          {leaderboard?.outlets?.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-xs" data-testid="full-leaderboard-table">
                <thead>
                  <tr style={{ backgroundColor: '#F7F5F2' }}>
                    <th className="text-left px-2 py-2 font-medium" style={{ color: '#6B7280' }}>#</th>
                    <th className="text-left px-2 py-2 font-medium" style={{ color: '#6B7280' }}>Outlet</th>
                    {!divisionId && <th className="text-left px-2 py-2 font-medium hidden sm:table-cell" style={{ color: '#6B7280' }}>Division</th>}
                    <th className="text-right px-2 py-2 font-medium" style={{ color: '#6B7280' }}>Actual</th>
                    <th className="text-right px-2 py-2 font-medium" style={{ color: '#6B7280' }}>Target</th>
                    <th className="text-right px-2 py-2 font-medium" style={{ color: '#6B7280' }}>%</th>
                  </tr>
                </thead>
                <tbody className="divide-y" style={{ borderColor: '#F3F4F6' }}>
                  {leaderboard.outlets.map((o, i) => {
                    const barColor = o.pct >= 30 ? '#2D8B5E' : o.pct >= 15 ? '#BA7517' : '#D4726A';
                    return (
                      <tr key={o.outlet}>
                        <td className="px-2 py-2 font-bold" style={{ color: i < 3 ? '#BA7517' : '#9CA3AF' }}>{o.rank}</td>
                        <td className="px-2 py-2 font-medium" style={{ color: '#1B2A4A' }}>
                          {o.outlet}
                          {o.owner && <span className="text-[9px] ml-1" style={{ color: '#9CA3AF' }}>({o.owner})</span>}
                        </td>
                        {!divisionId && <td className="px-2 py-2 hidden sm:table-cell" style={{ color: '#6B7280' }}>{o.division_name}</td>}
                        <td className="px-2 py-2 text-right font-medium" style={{ color: '#1B2A4A' }}>{fmtCurrency(o.actual)}</td>
                        <td className="px-2 py-2 text-right" style={{ color: '#6B7280' }}>{fmtCurrency(o.target)}</td>
                        <td className="px-2 py-2 text-right">
                          <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full"
                            style={{ backgroundColor: barColor + '15', color: barColor }}>
                            {o.pct}%
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              <p className="text-[9px] mt-2 text-right" style={{ color: '#9CA3AF' }}>
                {leaderboard.date_from} to {leaderboard.date_to}
              </p>
            </div>
          ) : (
            <p className="text-xs text-center py-12" style={{ color: '#9CA3AF' }}>No outlet data for this period</p>
          )}
        </div>
      </div>
    </div>
  );
}


// --- People Performance Warnings ---
export function PeopleWarnings({ divisionId }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);

  useEffect(() => {
    const params = divisionId ? `?division_id=${divisionId}` : '';
    api.get(`/people/warnings${params}`).then(r => setData(r.data)).catch(() => {});
  }, [divisionId]);

  if (!data || !data.warnings?.length) return null;

  const severityIcons = {
    critical: <AlertTriangle size={12} style={{ color: '#D4726A' }} />,
    warning: <Clock size={12} style={{ color: '#BA7517' }} />,
    info: <Heart size={12} style={{ color: '#6B7280' }} />,
  };
  const severityBg = { critical: '#FEF2F2', warning: '#FEF3C7', info: '#F7F5F2' };

  return (
    <div className="bg-white rounded-[10px] border p-4" style={{ borderColor: '#E5E1DC' }} data-testid="people-warnings">
      <div className="flex items-center gap-2 mb-3">
        <UserX size={14} style={{ color: '#D4726A' }} />
        <h4 className="text-sm font-semibold" style={{ color: '#1B2A4A' }}>People Alerts</h4>
        <span className="text-[10px] px-1.5 py-0.5 rounded-full font-bold" style={{ backgroundColor: '#FEF2F2', color: '#D4726A' }}>
          {data.total}
        </span>
      </div>
      <div className="space-y-1.5 max-h-48 overflow-y-auto">
        {data.warnings.slice(0, 10).map((w, i) => (
          <div key={i} onClick={() => w.person_id && navigate(`/people/${w.person_id}`)}
            className="flex items-start gap-2 p-2 rounded-md cursor-pointer hover:opacity-80 transition-opacity"
            style={{ backgroundColor: severityBg[w.severity] || '#F7F5F2' }}>
            {severityIcons[w.severity] || severityIcons.info}
            <div className="flex-1 min-w-0">
              <p className="text-xs leading-snug" style={{ color: '#1B2A4A' }}>{w.message}</p>
              <p className="text-[9px]" style={{ color: '#9CA3AF' }}>
                {!divisionId && w.division_name ? `${w.division_name} · ` : ''}{w.outlet || w.location || w.role || ''}
              </p>
            </div>
          </div>
        ))}
        {data.warnings.length > 10 && (
          <p className="text-[10px] text-center" style={{ color: '#9CA3AF' }}>+{data.warnings.length - 10} more</p>
        )}
      </div>
    </div>
  );
}


// --- Wins & Momentum ---
const WIN_ICONS = {
  scaled: { icon: '🎯', bg: '#ECFDF5', color: '#2D8B5E', label: 'SCALED' },
  near_scale: { icon: '🔥', bg: '#FEF3C7', color: '#BA7517', label: 'NEAR SCALE' },
  on_track: { icon: '✓', bg: '#E6F1FB', color: '#3B5BA9', label: 'ON TRACK' },
  momentum: { icon: '📈', bg: '#F0FDF4', color: '#2D8B5E', label: 'MOMENTUM' },
  streak: { icon: '⚡', bg: '#FFFBEB', color: '#D97706', label: 'STREAK' },
};

export function WinsAndMomentum({ divisionId }) {
  const [data, setData] = useState(null);

  useEffect(() => {
    const params = divisionId ? `?division_id=${divisionId}` : '';
    api.get(`/daily-sales/wins${params}`).then(r => setData(r.data)).catch(() => {});
  }, [divisionId]);

  if (!data || !data.wins?.length) return null;

  return (
    <div className="bg-white rounded-[10px] border p-4" style={{ borderColor: '#E5E1DC' }} data-testid="wins-momentum">
      <div className="flex items-center gap-2 mb-3">
        <TrendingUp size={14} style={{ color: '#2D8B5E' }} />
        <h4 className="text-sm font-semibold" style={{ color: '#1B2A4A' }}>Wins & Momentum</h4>
        <span className="text-[10px] px-1.5 py-0.5 rounded-full font-bold" style={{ backgroundColor: '#ECFDF5', color: '#2D8B5E' }}>
          {data.total}
        </span>
      </div>
      <div className="space-y-1.5 max-h-56 overflow-y-auto">
        {data.wins.map((w, i) => {
          const style = WIN_ICONS[w.type] || WIN_ICONS.on_track;
          return (
            <div key={i} className="flex items-start gap-2 p-2 rounded-md" style={{ backgroundColor: style.bg }}>
              <span className="text-sm shrink-0 mt-0.5">{style.icon}</span>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5">
                  <span className="text-[9px] font-bold px-1 py-0.5 rounded" style={{ backgroundColor: style.color, color: '#fff' }}>
                    {style.label}
                  </span>
                  {w.pct && <span className="text-[10px] font-semibold" style={{ color: style.color }}>{w.pct}%</span>}
                </div>
                <p className="text-xs leading-snug mt-0.5" style={{ color: '#1B2A4A' }}>{w.message}</p>
                {!divisionId && w.division && (
                  <p className="text-[9px]" style={{ color: '#9CA3AF' }}>{w.division}</p>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
